import React, { Fragment, useMemo } from 'react';
import type { Formik } from 'formik';
import { get } from 'lodash';
import type { ValidatorsType } from 'Components/widgets/contact-info/ContactInfoUtils';
import type { State as userInfoType } from 'Store/user-info/reducers/userInfoReducer';
import 'Store/user-info/reducers/userInfoReducer';
import { shouldHideField } from 'Components/widgets/contact-info/ContactInfoUtils';
import { t } from 'Src/i18n';
import { useGQL } from 'Utils/graphql-client/use-gql/useGQL';
import { getCountriesQuery } from 'Utils/graphql-query-helper/countries/countries.queries';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';

import { FormikInput } from 'Components/frameworks/formik/FormikInput';
import { UserAddressForm } from 'Components/widgets/user-address-form/UserAddressForm';
import { useAppContext } from 'Context/ReactAppContext';

type Props = {
  addressSchema: Array<ValidationSchema>;
  attendeeIdentifier: string;
  connectedUserInfo?: userInfoType;
  formik: Formik;
  hasAddress: boolean;
  onKeyDown?: (e: KeyboardEvent) => void;
  validators: ValidatorsType;
  isMain: boolean;
  isChild: boolean;
  isEmailVisible: boolean;
  isEmailRequired: boolean;
  isPhonesVisible: boolean;
  isPhonesRequired: boolean;
  hideErrorMessage?: boolean;
};
export const AdditionalContactInfos = ({
  addressSchema,
  attendeeIdentifier,
  connectedUserInfo,
  formik,
  hasAddress,
  onKeyDown,
  validators,
  isMain,
  isChild,
  isEmailVisible,
  isEmailRequired,
  isPhonesVisible,
  isPhonesRequired,
  hideErrorMessage = false,
}: Props) => {
  const appContext = useAppContext();
  const shouldShowPhoneWithSeparatePhoneCodeField =
    isFeatureFlipped(appContext)('booking.participants.phone.showWithSeparatePhoneCodeField') ??
    false;
  const shouldFetchCountriesFromApi =
    isFeatureFlipped(appContext)(
      'booking.participants.address.shouldFetchCountryListFromApiCountries',
    ) ?? false;

  const needsCountriesData =
    shouldShowPhoneWithSeparatePhoneCodeField || shouldFetchCountriesFromApi;

  const { data: countriesData, isLoading: isLoadingCountries } = useGQL({
    query: getCountriesQuery(),
    queryOptions: {
      cacheFront: { key: 'REQUEST_COUNTRIES' },
      requestName: 'REQUEST_COUNTRIES',
    },
    cache: true,
    map: (res) => res.countries,
    ignore: !needsCountriesData,
  });

  const phoneCodeOptions = useMemo(() => {
    if (!countriesData) {
      return [{ value: '', name: '', shortName: '', countryCode: '' }];
    }
    const emptyOption = { value: '', name: '', shortName: '', countryCode: '' };
    const countryOptions = countriesData
      .filter((country) => country.phoneCode)
      .map((country) => ({
        value: country.phoneCode,
        name: `${country.name} (+${country.phoneCode})`,
        shortName: `${country.value} (+${country.phoneCode})`,
        countryCode: country.value,
      }));
    return [emptyOption, ...countryOptions];
  }, [countriesData]);

  return (
    <Fragment>
      {(isEmailVisible || isPhonesVisible) && (
        <fieldset className="mt-10 p-10 sm:flex sm:flex-wrap">
          {isEmailVisible && (
            <div className="p-10 sm:w-1/2">
              <FormikInput
                hideErrorMessage={hideErrorMessage}
                isDisabled={shouldHideField({
                  connectedUserInfo,
                  fieldName: 'email',
                  formInitialValues: formik.initialValues,
                  isMain,
                  isChild,
                })}
                isRequired={isEmailRequired}
                label={t('booking.participants.labels.email')}
                name={`${attendeeIdentifier}.email`}
                onKeyDown={onKeyDown}
                type="email"
                validate={isEmailRequired ? validators.email : validators.emailNotRequired}
              />
            </div>
          )}

          {isPhonesVisible &&
            (!shouldShowPhoneWithSeparatePhoneCodeField ||
              !countriesData ||
              isLoadingCountries) && (
              <div className="p-10 sm:w-1/2">
                <FormikInput
                  hideErrorMessage={hideErrorMessage}
                  isDisabled={
                    shouldHideField({
                      connectedUserInfo,
                      fieldName: 'mobile',
                      fieldNameConnectedUserOverride: 'mobilePhoneNumber',
                      isMain,
                      isChild,
                    }) || isLoadingCountries
                  }
                  isRequired={isPhonesRequired}
                  label={t('booking.participants.labels.phone')}
                  name={`${attendeeIdentifier}.mobile`}
                  onKeyDown={onKeyDown}
                  type="tel"
                  validate={isPhonesRequired ? validators.mobile : validators.mobileNotRequired}
                />
              </div>
            )}
        </fieldset>
      )}
      {isPhonesVisible &&
        shouldShowPhoneWithSeparatePhoneCodeField &&
        countriesData &&
        !isLoadingCountries && (
          <fieldset className="mt-10 p-10 sm:flex sm:flex-wrap">
            <div className="p-10">
              <FormikInput
                hideErrorMessage={hideErrorMessage}
                isDisabled={shouldHideField({
                  connectedUserInfo,
                  fieldName: 'mobile',
                  fieldNameConnectedUserOverride: 'mobilePhoneNumber',
                  isMain,
                  isChild,
                })}
                isRequired={isPhonesRequired}
                name={`${attendeeIdentifier}.mobile`}
                onKeyDown={onKeyDown}
                type="telWithCode"
                phoneCodeFieldName={`${attendeeIdentifier}.mobileCode`}
                phoneCodeLabel={t('common.participants.address.countryCode')}
                countryFieldName={
                  hasAddress ? `${attendeeIdentifier}.address.countryCode` : undefined
                }
                phoneCodeOptions={phoneCodeOptions}
                phoneCodePlaceholder=""
                label={t('booking.participants.labels.phone')}
                validate={isPhonesRequired ? validators.mobile : validators.mobileNotRequired}
              />
            </div>
          </fieldset>
        )}
      {isMain && !isChild && hasAddress && (
        <fieldset className="mt-10 p-10 sm:flex sm:flex-wrap">
          <UserAddressForm
            fields={addressSchema}
            fieldsValues={get(formik, `values.${attendeeIdentifier}.address`)}
            hideErrorMessage={hideErrorMessage}
            namePrefix={`${attendeeIdentifier}.address.`}
            onKeyDown={onKeyDown}
            validators={validators.address}
            fetchCountriesFromApi={shouldFetchCountriesFromApi}
            countriesData={countriesData || undefined}
            isLoadingCountries={isLoadingCountries}
          />
        </fieldset>
      )}
    </Fragment>
  );
};
